/** Daily note lines: `- [ ] ` and `- [x] ` start a to-do line (data dictionary §3, dailyNote). */

export const TodoMarker = {
  Open: '- [ ] ',
  Done: '- [x] ',
} as const;

export const NoteLineKind = {
  Todo: 'todo',
  Text: 'text',
} as const;
export type NoteLineKind = (typeof NoteLineKind)[keyof typeof NoteLineKind];

export interface NoteLine {
  readonly kind: NoteLineKind;
  /** Only for to-do lines. */
  readonly done?: boolean;
  /** To-do lines: the text after the marker. Text lines: the whole line. */
  readonly text: string;
  /** Zero-based line number in the note. */
  readonly index: number;
}

const TODO_LINE = /^- \[([ xX])\] ([\s\S]*)$/;

/** Line endings as the Markdown parser reads them: CRLF, a lone CR and LF each end a line. */
const LINE_BREAK = /\r\n?|\n/;
/** The same, keeping each ending in the split result (at odd positions). */
const LINE_BREAK_KEPT = /(\r\n?|\n)/;

export function parseNote(text: string): NoteLine[] {
  return text.split(LINE_BREAK).map((line, index): NoteLine => {
    const match = TODO_LINE.exec(line);
    if (match === null) return { kind: NoteLineKind.Text, text: line, index };
    return { kind: NoteLineKind.Todo, done: match[1] !== ' ', text: match[2] ?? '', index };
  });
}

/**
 * The note text with the to-do on `lineIndex` flipped; unchanged when that line is not a to-do.
 * Line endings (LF, CRLF, lone CR) are kept as they are.
 */
export function toggleTodo(text: string, lineIndex: number): string {
  // Even positions are lines, odd positions the line endings between them.
  const parts = text.split(LINE_BREAK_KEPT);
  const position = lineIndex * 2;
  const line = lineIndex < 0 ? undefined : parts[position];
  if (line === undefined) return text;
  const match = TODO_LINE.exec(line);
  if (match === null) return text;
  const marker = match[1] === ' ' ? TodoMarker.Done : TodoMarker.Open;
  return parts.map((current, index) => (index === position ? `${marker}${match[2] ?? ''}` : current)).join('');
}
